import { useEffect, useState } from "react";
import Masonry from "react-masonry-css";
import { Heart } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { AdCard } from "../components/AdCard";
import { AdDetailModal } from "../components/AdDetailModal";

const BREAKPOINTS = { default: 4, 1280: 3, 768: 2, 500: 1 };

export default function Favorites() {
  const navigate = useNavigate();
  const [ads, setAds] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/favorites").then((r) => setAds(r.data)).catch(() => setAds([]));
  }, []);

  const onFavoriteChange = (adId, isFav) => {
    if (!isFav) {
      setAds((prev) => prev?.filter((a) => a.id !== adId));
      setSelected((prev) => (prev && prev.id === adId ? null : prev));
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-10 flex-1">
        <div className="fade-up text-center">
          <h1 className="font-display font-black text-4xl sm:text-5xl text-[#000000] tracking-tight uppercase">Favorites</h1>
          <p className="text-gray-500 mt-2">Every creative you&rsquo;ve saved, in one place.</p>
        </div>
        {ads === null ? (
          <div className="flex justify-center py-24">
            <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-[#DF1283] spin-slow" />
          </div>
        ) : ads.length === 0 ? (
          <div className="text-center py-24" data-testid="favorites-empty-state">
            <Heart size={40} className="mx-auto text-gray-300" />
            <p className="font-display font-bold text-xl text-[#000000] mt-4">No favorites yet</p>
            <p className="text-gray-500 mt-2 text-sm">Tap the heart on any ad to save it here.</p>
            <Link to="/" data-testid="favorites-browse-link" className="inline-block mt-6 bg-[#000000] text-white rounded-full px-6 py-2.5 font-bold text-sm hover:bg-[#4A0A66] transition-colors">
              Browse the Ad Wall
            </Link>
          </div>
        ) : (
          <Masonry breakpointCols={BREAKPOINTS} className="masonry-grid mt-10" columnClassName="masonry-col">
            {ads.map((ad, i) => (
              <AdCard key={ad.id} ad={ad} index={i} categories={categories} onOpen={setSelected} onFavoriteChange={onFavoriteChange} />
            ))}
          </Masonry>
        )}
      </main>
      <Footer />
      {selected && (
        <AdDetailModal
          ad={selected}
          categories={categories}
          onClose={() => setSelected(null)}
          onFavoriteChange={onFavoriteChange}
          onNavigate={(dir) => {
            if (!ads?.length) return;
            const idx = ads.findIndex((a) => a.id === selected.id);
            const next = ads[idx + dir];
            if (next) setSelected(next);
          }}
          hasPrev={ads && ads.findIndex((a) => a.id === selected.id) > 0}
          hasNext={ads && ads.findIndex((a) => a.id === selected.id) < ads.length - 1}
          onCategoryClick={(catId) => navigate(`/?category=${catId}`)}
          onNoteCountChange={(adId, count) => {
            setAds((prev) => (prev ? prev.map((a) => (a.id === adId ? { ...a, note_count: count } : a)) : prev));
            if (selected && selected.id === adId) setSelected({ ...selected, note_count: count });
          }}
        />
      )}
    </div>
  );
}
